import { useEffect, useMemo, useState } from 'react';

type ImageLoadState = 'loading' | 'loaded' | 'error';

interface UseImageLoaderResult {
  image?: HTMLImageElement | null;
  loadState: ImageLoadState;
}

// Module-level cache for loaded images - persists across component remounts
const imageCache = new Map<string, HTMLImageElement>();

// Extract base URL without query params (signature)
function getBaseUrl(url: string): string {
  return url?.split('?')[0] ?? '';
}

export function useImageLoader(imageUrl: string): UseImageLoaderResult {
  const baseUrl = useMemo(() => getBaseUrl(imageUrl), [imageUrl]);

  // Handle empty URL case
  const isEmptyUrl = !imageUrl || imageUrl.trim() === '';

  // Check cache first - this survives remounts
  const cachedImage = isEmptyUrl ? undefined : imageCache.get(baseUrl);

  const [image, setImage] = useState<HTMLImageElement | null>(
    cachedImage ?? null
  );
  const [loadState, setLoadState] = useState<ImageLoadState>(
    isEmptyUrl ? 'error' : cachedImage ? 'loaded' : 'loading'
  );

  useEffect(() => {
    // Return early if URL is empty
    if (isEmptyUrl) {
      setImage(null);
      setLoadState('error');
      return;
    }

    // Already have this image cached
    const cached = imageCache.get(baseUrl);
    if (cached) {
      setImage(cached);
      setLoadState('loaded');
      return;
    }

    setLoadState('loading');

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => {
      imageCache.set(baseUrl, img);
      setImage(img);
      setLoadState('loaded');
    };
    img.onerror = () => {
      setLoadState('error');
    };
  }, [baseUrl, imageUrl, isEmptyUrl]);

  return { image, loadState };
}
